import { faCheck, faClose, faSpinnerThird } from "@fortawesome/pro-regular-svg-icons";
// import { faSpinnerThird } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { colorPalette, cssBody400, cssBody600Bold, NewButton } from "@helium10/re-ui-components";
import { memo, useMemo } from "react";
import styled from "styled-components";

import { useTranslation } from "@/i18n/useTranslation";
import { useUploadFilesStore, MAX_SINGLE_FILE_SIZE } from "@/components/UploadFiles/store";
import { useUploadHistoryStore } from "@/components/UploadHistory/store";
import { documentCenterRoute } from "@/core/links";
import { getUrlWithSearchParams } from "@helium10/re-core";
import { getNavigate } from "@/utils/navigateService";

const BULK_UPLOAD_TASK_PREFIX = "document-center-batch-";

export const BatchUploadBanner = memo(() => {
  const { t } = useTranslation();
  const tasks = useUploadFilesStore((s) => s.tasks);
  const clearBulkUploadTasks = useUploadFilesStore((s) => s.clearBulkUploadTasks);

  const { totalFiles, completedFiles, isUploading } = useMemo(() => {
    const bulkTasks = Object.entries(tasks).filter(([id]) =>
      id.startsWith(BULK_UPLOAD_TASK_PREFIX),
    );
    let total = 0;
    let completed = 0;
    let uploading = false;

    for (const [, task] of bulkTasks) {
      if (task.status === "uploading") {
        uploading = true;
      }
      // 仅统计实际上传的文件（≤10MB），与 UploadDrawer Footer 的 validFileList 一致
      // blob 缺失时视为无效
      const validFiles = task.fileList.filter(
        (f) => (f.file?.blob?.size ?? Infinity) <= MAX_SINGLE_FILE_SIZE,
      );
      total += validFiles.length;
      for (const file of validFiles) {
        if (file.status === "loaded" || file.status === "error") {
          completed += 1;
        }
      }
    }

    return {
      totalFiles: total,
      completedFiles: completed,
      isUploading: uploading,
    };
  }, [tasks]);

  const handleViewDetail = () => {
    const navigate = getNavigate();
    navigate(getUrlWithSearchParams(documentCenterRoute, {}, { hasAccountId: true }));
    useUploadHistoryStore.getState().openDrawer();
    clearBulkUploadTasks();
  };

  const handleClose = () => {
    clearBulkUploadTasks();
  };

  // 只在 task 为 "uploading" 或已完成时显示，init 时不展示
  const shouldShow = isUploading || totalFiles > 0; // && completedFiles <= totalFiles
  if (!shouldShow) {
    return null;
  }

  return (
    <BannerWrapper $isUploading={isUploading}>
      {isUploading ? (
        <UploadingWrapper>
          <TitleWrapper>
            <StyledIcon icon={faSpinnerThird} spin />
            <Title>
              {t("batchUploadBanner.uploading.title", {
                completed: completedFiles,
                total: totalFiles,
              })}
            </Title>
          </TitleWrapper>
          <Subtitle>{t("batchUploadBanner.uploading.subtitle")}</Subtitle>
        </UploadingWrapper>
      ) : (
        <CompletedWrapper>
          <Wrapper>
            <TitleWrapper>
              <StyledIcon icon={faCheck} />
              <Title>
                {t("batchUploadBanner.completed.title", {
                  completed: completedFiles,
                  total: totalFiles,
                })}
              </Title>
            </TitleWrapper>
            <Subtitle>{t("batchUploadBanner.completed.subtitle")}</Subtitle>
          </Wrapper>
          <Actions>
            <NewButton buttonType="primary" size="100" onClick={handleViewDetail}>
              {t("batchUploadBanner.completed.viewDetail")}
            </NewButton>
            <CloseIcon icon={faClose} onClick={handleClose} />
          </Actions>
        </CompletedWrapper>
      )}
    </BannerWrapper>
  );
});

const BannerWrapper = styled.div<{ $isUploading: boolean }>`
  position: relative;
  z-index: 6;
  width: 100%;
  padding: 12px 24px 12px 48px;
  background-color: ${({ theme, $isUploading }) =>
    $isUploading ? theme.systemColors.blue50 : colorPalette.green50};
`;
const UploadingWrapper = styled.div``;
const CompletedWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const Wrapper = styled.div``;
const TitleWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 18px;
`;
const StyledIcon = styled(FontAwesomeIcon)`
  color: ${({ theme }) => theme.systemColors.blue600};
`;
const Title = styled.div`
  ${cssBody600Bold}
  font-weight: 800;
`;
const Subtitle = styled.div`
  ${cssBody400}
  font-weight: 400;
  color: ${({ theme }) => theme.systemColors.black};
`;
const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sp12};
`;
const CloseIcon = styled(FontAwesomeIcon)`
  cursor: pointer;
`;
